import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getSubscription,
  cancelSubscription,
  resumeSubscription,
} from '@/services/subscription'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/use-toast'
import { Loader2, CheckCircle, XCircle, RefreshCw } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Badge } from '@/components/ui/badge'

type Subscription = Awaited<ReturnType<typeof getSubscription>>

const SubscriptionStatusBadge = ({ status }: { status: string | null }) => {
  switch (status) {
    case 'active':
      return (
        <Badge variant="outline" className="border-green-500 text-green-400">
          <CheckCircle className="mr-2 h-4 w-4" />
          Ativa
        </Badge>
      )
    case 'trialing':
      return (
        <Badge variant="outline" className="border-yellow-500 text-yellow-400">
          <CheckCircle className="mr-2 h-4 w-4" />
          Período de Teste
        </Badge>
      )
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

const SubscriptionManagementPage = () => {
  const [subscription, setSubscription] = useState<Subscription>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const { toast } = useToast()
  const navigate = useNavigate()

  const fetchSubscription = useCallback(async () => {
    setIsLoading(true)
    try {
      const sub = await getSubscription()
      if (!sub) {
        toast({
          title: 'Nenhuma assinatura ativa encontrada.',
          description: 'Redirecionando...',
          variant: 'destructive',
        })
        navigate('/subscribe')
      } else {
        setSubscription(sub)
      }
    } catch (error) {
      toast({
        title: 'Erro ao buscar dados da assinatura.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }, [navigate, toast])

  useEffect(() => {
    fetchSubscription()
  }, [fetchSubscription])

  const handleCancel = async () => {
    setIsProcessing(true)
    try {
      await cancelSubscription()
      toast({
        title: 'Assinatura será cancelada.',
        description:
          'Sua assinatura permanecerá ativa até o final do período de cobrança.',
      })
      await fetchSubscription()
    } catch (error) {
      toast({
        title: 'Erro ao cancelar assinatura.',
        variant: 'destructive',
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const handleResume = async () => {
    setIsProcessing(true)
    try {
      await resumeSubscription()
      toast({
        title: 'Assinatura reativada!',
        description: 'Sua assinatura continuará ativa.',
      })
      await fetchSubscription()
    } catch (error) {
      toast({
        title: 'Erro ao reativar assinatura.',
        variant: 'destructive',
      })
    } finally {
      setIsProcessing(false)
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-2xl py-10 px-4">
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-1/2" />
            <Skeleton className="h-4 w-1/3 mt-2" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-2/3" />
          </CardContent>
          <CardFooter>
            <Skeleton className="h-10 w-40" />
          </CardFooter>
        </Card>
      </div>
    )
  }

  if (!subscription) {
    return null
  }

  const planName = subscription.prices?.products?.name || 'Plano Desconhecido'
  const planPrice = subscription.prices?.unit_amount
    ? `R${(subscription.prices.unit_amount / 100).toFixed(2)}`
    : ''
  const planInterval = subscription.prices?.interval === 'month' ? '/mês' : ''
  const renewalDate = subscription.current_period_end
    ? format(
        new Date(subscription.current_period_end),
        "dd 'de' MMMM 'de' yyyy",
        {
          locale: ptBR,
        },
      )
    : 'N/A'
  const cancelAtDate = subscription.cancel_at
    ? format(new Date(subscription.cancel_at), "dd 'de' MMMM 'de' yyyy", {
        locale: ptBR,
      })
    : renewalDate

  return (
    <div className="container mx-auto max-w-2xl py-10 px-4">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl">Gerenciar Assinatura</CardTitle>
            <SubscriptionStatusBadge status={subscription.status} />
          </div>
          <CardDescription>
            Visualize e gerencie os detalhes do seu plano.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center p-4 border rounded-md">
            <span className="font-medium">Plano</span>
            <span>{`${planName} (${planPrice}${planInterval})`}</span>
          </div>
          <div className="flex justify-between items-center p-4 border rounded-md">
            <span className="font-medium">
              {subscription.cancel_at_period_end
                ? 'Expira em'
                : 'Próxima Cobrança'}
            </span>
            <span>{renewalDate}</span>
          </div>
          {subscription.cancel_at_period_end && (
            <div className="p-4 border border-yellow-500/50 bg-yellow-950 rounded-md text-yellow-300">
              Sua assinatura está programada para ser cancelada em{' '}
              <strong>{cancelAtDate}</strong>. Você terá acesso aos recursos
              premium até essa data.
            </div>
          )}
        </CardContent>
        <CardFooter>
          {subscription.cancel_at_period_end ? (
            <Button onClick={handleResume} disabled={isProcessing}>
              {isProcessing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reativar Assinatura
                </>
              )}
            </Button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={isProcessing}>
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancelar Assinatura
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Tem certeza que deseja cancelar?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Sua assinatura permanecerá ativa até {renewalDate}. Após
                    essa data, você perderá o acesso aos recursos premium. Você
                    pode reativar a qualquer momento antes do final do período.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Manter Assinatura</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleCancel}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isProcessing ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      'Confirmar Cancelamento'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </CardFooter>
      </Card>
    </div>
  )
}

export default SubscriptionManagementPage
